import { createAction } from 'redux-actions';

import {
  login, logout, askResetUserPassword, resetUserPassword,
} from '@app/services/authenticationService';

import { checkSubscriptionValidity, resetUserOptions } from '@app/actions/subscribeActions';
import { requestRecaptureUpdate } from '@app/actions/recaptchaActions';
import { getResetPasswordParams } from '@app/helpers/resetPasswordHelper';
import { isRecaptchaShown } from '@app/helpers/recaptchaHelpers';

export const successLogin = createAction('USER/SUCCESS_LOGIN');
export const tokenUpdate = createAction('USER/UPDATE_TOKEN');
export const deviceTokenUpdate = createAction('USER/UPDATE_DEVICE_TOKEN');
export const reset = createAction('USER/RESET');
export const setFailedLoginAttemptsNumber = createAction('USER/SET_FAILED_LOGIN_ATTEMPTS_NUMBER');
export const setError = createAction('USER/SET_ERROR');
export const notifyUserNotSupported = createAction('USER/NOTIFY_USER_NOT_SUPPORTED');
export const successAskResetPassword = createAction('USER/SUCCESS_ASK_RESET_PASSWORD');
export const successResetPassword = createAction('USER/SUCCESS_RESET_PASSWORD');
export const resetAskForgotPasswordPage = createAction('USER/RESET_ASK_FORGOT_PASSWORD_PAGE');
export const resetForgotPasswordPage = createAction('USER/RESET_FORGOT_PASSWORD_PAGE');

/**
 * User: Restore session and detect real geo-location
 */
export function detectUser(dispatch) {
  const authToken = localStorage.getItem('authToken');
  const failedLoginAttemptsNumber = localStorage.getItem('failedLoginAttemptsNumber');

  if (authToken) {
    dispatch(updateToken(authToken));
    dispatch(loginSuccess());
  }

  dispatch(setFailedLoginAttemptsNumber(Number(failedLoginAttemptsNumber || 0)));
}

export function loginUser({ email, password }) {
  return (dispatch, getState) => {
    const captchaToken = getState().recaptcha.token;

    return login({ email, password, captchaToken })
      .then(() => {
        localStorage.setItem('failedLoginAttemptsNumber', '0');
        dispatch(loginSuccess());
      })
      .catch((error) => {
        console.error('Error on log in: ', error);

        dispatch(loginFailure(error));

        localStorage.removeItem('authToken');
        localStorage.removeItem('deviceToken');
      });
  };
}

function loginFailure(error) {
  return (dispatch, getState) => {
    const { user: { failedLoginAttemptsNumber } } = getState();

    if (isRecaptchaShown(failedLoginAttemptsNumber)) {
      dispatch(requestRecaptureUpdate());
    }

    const newFailedLoginAttemptsNumber = failedLoginAttemptsNumber + 1;

    localStorage.setItem('failedLoginAttemptsNumber', (newFailedLoginAttemptsNumber).toString());
    localStorage.removeItem('authToken');
    localStorage.removeItem('deviceToken');

    dispatch(setError(error));
    dispatch(setFailedLoginAttemptsNumber(newFailedLoginAttemptsNumber));
  };
}

function loginSuccess() {
  return (dispatch) => {
    dispatch(successLogin());
    dispatch(checkSubscriptionValidity());
  };
}

export function userLogout() {
  return (dispatch, getState) => {
    const { user: { authToken } } = getState();

    dispatch(resetUser());

    logout({ authToken })
      .catch((error) => {
        console.error('Error on log in: ', error);
      });
  };
}

export function updateToken(token) {
  return (dispatch) => {
    localStorage.setItem('authToken', token);
    dispatch(tokenUpdate(token));
  };
}

export function updateDeviceToken(token) {
  return (dispatch) => {
    localStorage.setItem('deviceToken', token);
    dispatch(deviceTokenUpdate(token));
  };
}

/**
 * User: Reset user data and session
 */
export function resetUser() {
  return (dispatch) => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('deviceToken');

    // Todo: reset other user data (subscriptions, account) when appropriate actions are implemented
    dispatch(reset());
    dispatch(resetUserOptions());
  };
}

export function askResetPassword(params) {
  return dispatch => (
    askResetUserPassword(params)
      .then(() => dispatch(successAskResetPassword()))
      .catch((error) => {
        console.error('Error on log in: ', error);
        return dispatch(setError(error));
      })
  );
}

export function resetPassword(formData) {
  return (dispatch) => {
    const params = {
      ...formData,
      ...getResetPasswordParams(document.location),
    };
    params.emailConfirm = params.email;
    return (
      resetUserPassword(params)
        .then(() => dispatch(successResetPassword()))
        .catch((error) => {
          console.error('Error on log in: ', error);
          dispatch(setError(error));
        })
    );
  };
}
